import Box from '@mui/material/Box';
import TextField, { type TextFieldProps } from '@mui/material/TextField';
import { type Control, Controller, type FieldPath, type FieldValues } from 'react-hook-form';

import { InfoHint } from '@/components/atoms';

export type SelectOption = { value: string; label: string };

type Props<T extends FieldValues, TOut = T> = {
  control: Control<T, unknown, TOut>;
  name: FieldPath<T>;
  label: string;
  options: readonly SelectOption[];
  /** Label of the empty choice, e.g. "Any brand". Omit when every value is valid. */
  emptyLabel?: string;
  disabled?: boolean;
  /** Shown under the field when there is no error. */
  helperText?: string;
  /** A sentence explaining the choice, offered behind a hint button. */
  help?: string;
  size?: TextFieldProps['size'];
  onValueChange?: (value: string) => void;
};

/**
 * Native select bound to React Hook Form: the platform picker on phones, full
 * keyboard support, and errors announced through the helper text (§39, §50).
 */
export function SelectField<T extends FieldValues, TOut = T>({
  control,
  name,
  label,
  options,
  emptyLabel,
  disabled = false,
  helperText,
  help,
  size = 'medium',
  onValueChange,
}: Props<T, TOut>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const message = fieldState.error?.message ?? helperText;
        /* A row, not a run of inline content: inline, the hint button is the
           last word of the helper text and drops onto a line of its own the
           moment that text wraps — which on a phone is almost every field,
           and reads as a stray icon rather than as help. Pinned to the
           field's trailing edge it keeps the row's height, stays beside the
           words it explains, and lines the hints up into one scannable rail
           down the form. */
        const helper =
          message || help ? (
            <Box
              component="span"
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
            >
              <Box component="span">{message}</Box>
              {help && <InfoHint label={label}>{help}</InfoHint>}
            </Box>
          ) : undefined;

        return (
          <TextField
            select
            label={label}
            name={field.name}
            value={field.value ?? ''}
            onChange={(e) => {
              field.onChange(e.target.value);
              onValueChange?.(e.target.value);
            }}
            onBlur={field.onBlur}
            inputRef={field.ref}
            disabled={disabled}
            size={size}
            error={Boolean(fieldState.error)}
            helperText={helper}
            slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}
          >
            <option value="">{emptyLabel}</option>
            {options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </TextField>
        );
      }}
    />
  );
}
